import React, { useState } from 'react'
import {useTranslation} from 'react-i18next'

import {
    DefaultButton,
    IconButton,
    IIconProps,
    Modal,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"

import {contentStyles, iconButtonStyles} from "./Style"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"

import {FormProvider, useForm} from "react-hook-form"
import {useBranding} from '../../hooks/useBranding'
import {RHFTextBoxControl} from "../RHFControls/RHFTextBoxControl"
import ApprovalService, {
    IGetApprovalStepActionStateResponse,
    IPostApprovalOverrideRequest
} from "../../services/Approval"
import {EApprovalCommand} from "./Approval.data"
import {decode} from 'html-entities'

type Properties = {
    command: EApprovalCommand
    currentAction: IGetApprovalStepActionStateResponse | null,
    showModal: boolean,
    onDismissModal: (update: boolean) => void,
    title: string

}

export function ApprovalOverride({
                                     command,
                                     currentAction,
                                     onDismissModal,
                                     showModal,
                                     title
                                 }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()
    const form = useForm({
        defaultValues: {
            Note: '',
        }
    })

    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)

    /**
     * Displays a status message with the specified content and status type.
     *
     * @function ShowStatusMessage
     * @param {string} message - The message text to be displayed.
     * @param {StatusEnum} status - An enumeration representing the type or status of the message (e.g., success, error).
     * @returns {void}
     */
    const ShowStatusMessage = (message: string, status: StatusEnum): void => {
        setMessage(decode(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * Asynchronous function to handle the creation of an approval step request.
     *
     * This function interacts with a form, retrieves form values, validates them, and submits validation errors if present.
     * Upon successful form submission, it sends a POST request to the ApprovalService to create an approval step.
     * Proper success or error messages are displayed to the user depending on the result of the request.
     *
     * The request data includes approval ID, name, and template ID.
     * The function also manages the saving state indicator and handles any errors that may occur during the request.
     *
     * Error messages and success statuses are displayed using localized strings from a translation function (`t`)
     * with appropriate namespaces (`ns`).
     *
     * @async
     * @function override
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    const override = async (): Promise<void> => {
        form.clearErrors()
        await form.handleSubmit(async () => {

                setIsSaving(true)

                // await AddNote().then()

                const requestKIM: IPostApprovalOverrideRequest = {
                    TemplateId: String(currentAction!.TemplateId ?? ''),
                    Note: form.getValues("Note"),
                    DocumentId: String(currentAction!.DocumentId ?? ''),
                    ApprovalStepActionId: String(currentAction!.ApprovalStepActionId ?? ''),
                    Status: EApprovalCommand[command]
                }

                await ApprovalService.postApprovalOverride(requestKIM)
                    .then(data => {
                        if (data.Success) {
                            onDismissModal(true)
                        } else {
                            ShowStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )();
    }

    return (
        <>


            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerOverride}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text
                                variant={"xLarge"} id={'approvalStepEditTitle'}
                                title={title}>
                                {title}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={() => {
                                    onDismissModal(false)
                                }}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {showMessage &&
                                        <>
                                            <StackItem styles={{root: {marginBottom: 16}}}>

                                                <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}
                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </StackItem>

                                        </>
                                    }

                                    <FormProvider {...form}>
                                        <form onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}>
                                            <Stack>
                                                <StackItem grow={1}>

                                                    <Text variant={"medium"} block styles={{root: {paddingBottom: 8, fontStyle: 'italic'}}}>
                                                        {t('text.noteOverride', {ns: 'approval'})}
                                                    </Text>

                                                    <RHFTextBoxControl
                                                        id={"Note"}
                                                        label={t('title.note', {ns: 'approval'})}
                                                        styles={{field: {height: 80}}}
                                                        rules={{
                                                            required: t('text.fieldRequired', {ns: 'common'}),
                                                            validate: {
                                                                required:
                                                                    (value) => {
                                                                        return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                                    }
                                                            },
                                                        }}

                                                        placeholder={t('title.note', {ns: 'approval'})}
                                                        title={t('title.note', {ns: 'approval'})}
                                                        ariaLabel={t('title.note', {ns: 'approval'})}
                                                        maxLength={2000}
                                                        control={form.control}
                                                        setValue={form.setValue}
                                                        getValues={form.getValues}
                                                        trigger={form.trigger}
                                                        disabled={isSaving}
                                                        multiline={true}

                                                        required
                                                    >
                                                    </RHFTextBoxControl>


                                                </StackItem>

                                            </Stack>

                                        </form>
                                    </FormProvider>

                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    {isSaving &&
                                        <Stack>
                                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                     label={t('text.spinnerSaving', {ns: 'common'})}/>
                                        </Stack>

                                    }

                                    <PrimaryButton disabled={isSaving}
                                                   style={{marginLeft: 8}}
                                                   onClick={override}
                                                   text={t('text.override', {ns: 'approval'})}
                                                   title={t('text.override', {ns: 'approval'})}
                                    />


                                    <DefaultButton
                                        onClick={() => {
                                            onDismissModal(false)
                                        }}
                                        style={{marginLeft: 8}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>

        </>
    )
}
